import { useMemo } from 'react';
import { Polyline, Tooltip } from 'react-leaflet';
import { useTrack } from '../context/TrackContext';

interface LTV {
    id: string;
    startPK: number;
    endPK: number;
    speed: number;
    reason: string;
    startDate: string;
    endDate: string;
    line?: 'Centrale' | 'Balagne';
}

interface LTVMapLayerProps {
    ltvList: LTV[];
}

export default function LTVMapLayer({ ltvList }: LTVMapLayerProps) {
    const { balagneSystem, centraleSystem } = useTrack();

    const ltvSegments = useMemo(() => {
        const segments: { item: LTV, positions: [number, number][], id: string }[] = [];

        ltvList.forEach(ltv => {
            if (ltv.startPK === null || ltv.startPK === undefined || isNaN(ltv.startPK) ||
                ltv.endPK === null || ltv.endPK === undefined || isNaN(ltv.endPK)) {
                return;
            }
            const maxPK = Math.max(ltv.startPK, ltv.endPK);
            const minPK = Math.min(ltv.startPK, ltv.endPK);

            const pointsBalagne = balagneSystem.pkToPath(minPK, maxPK);
            const pointsCentrale = centraleSystem.pkToPath(minPK, maxPK);

            if (ltv.line === 'Balagne') {
                if (pointsBalagne.length > 0) {
                    segments.push({ item: ltv, positions: pointsBalagne, id: ltv.id + '-bal' });
                }
            } else if (ltv.line === 'Centrale') {
                if (pointsCentrale.length > 0) {
                    segments.push({ item: ltv, positions: pointsCentrale, id: ltv.id + '-cen' });
                }
            } else {
                // Heuristic fallback for older LTVs without explicit line field
                const inBalagneRange = maxPK <= 119.9 && minPK >= 46.7;
                const inCentraleRange = maxPK <= 157.4 && minPK >= 0;

                if (inBalagneRange && pointsBalagne.length > 0) {
                    segments.push({ item: ltv, positions: pointsBalagne, id: ltv.id + '-bal' });
                }
                if (inCentraleRange && pointsCentrale.length > 0) {
                    segments.push({ item: ltv, positions: pointsCentrale, id: ltv.id + '-cen' });
                }
            }
        });

        return segments;
    }, [ltvList, balagneSystem, centraleSystem]);

    // Masquer les LTV uniquement pendant la calibration active d'une gare pour éviter la surcharge visuelle


    const formatDate = (d: string) => {
        if (!d || d === 'RESTITUTION') return d;
        if (d.includes('-')) {
            const [y, m, day] = d.split('-');
            return `${day}/${m}/${y}`;
        }
        return d;
    };

    return (
        <>
            {ltvSegments.map((seg) => {
                const speedNum = Number(seg.item.speed);
                const isYellowFlag = isNaN(speedNum) || speedNum === 0;

                const primaryColor = isYellowFlag ? '#eab308' : '#ef4444';
                const glowOpacity = isYellowFlag ? 0.45 : 0.3;

                return (
                    <div key={seg.id}>
                        {/* Effet de lueur (glow) en arrière-plan */}
                        <Polyline
                            positions={seg.positions}
                            pathOptions={{ color: primaryColor, weight: 14, opacity: glowOpacity }}
                        />
                        {/* Ligne principale pointillée */}
                        <Polyline
                            positions={seg.positions}
                            pathOptions={{ color: primaryColor, weight: 6, opacity: 0.95, dashArray: '8, 12' }}
                        >
                            <Tooltip sticky>
                                <div className="font-sans text-xs">
                                    <strong className={`${isYellowFlag ? 'text-yellow-600' : 'text-red-600'} block mb-1 uppercase tracking-tighter`}>
                                        {isYellowFlag ? '⚠️ POSSIBLE RALENTISSEMENT (DRAPEAU JAUNE)' : `⚠️ RALENTISSEMENT ${seg.item.speed} KM/H`}
                                    </strong>
                                    <div className={`border-t ${isYellowFlag ? 'border-yellow-500/30' : 'border-red-500/20'} my-1 pt-1`}>
                                        <p className="font-bold text-slate-700">PK {seg.item.startPK.toFixed(3)} - {seg.item.endPK.toFixed(3)}</p>
                                        <p className="italic text-slate-500 mt-1">« {seg.item.reason} »</p>
                                        <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold">{formatDate(seg.item.startDate)} AU {formatDate(seg.item.endDate)}</p>
                                    </div>
                                </div>
                            </Tooltip>
                        </Polyline>
                    </div>
                );
            })}
        </>
    );
}
